import { apiClient, unwrap } from '@/lib/api-client';
import { tokenStorage } from '@/lib/token-storage';
import type { ApiSuccess, AuthUser, LoginResponse } from '@/types/api';

/**
 * Dua jalur masuk, bukan satu kolom serbaguna.
 *
 * Staf memakai surel, yang unik lintas lembaga. Wali murid memakai NIS
 * anaknya, yang hanya unik DI DALAM satu lembaga — karena itu jalur wali wajib
 * menyertakan kode sekolah, dan tipe ini memaksanya ikut terkirim.
 */
export type LoginPayload =
  | { email: string; password: string }
  | { username: string; tenantSlug: string; password: string };

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export const authService = {
  login(payload: LoginPayload): Promise<LoginResponse> {
    return unwrap(apiClient.post<ApiSuccess<LoginResponse>>('/auth/login', payload));
  },

  getProfile(): Promise<AuthUser> {
    return unwrap(apiClient.get<ApiSuccess<AuthUser>>('/auth/me'));
  },

  /**
   * Field disebut satu per satu, bukan meneruskan `payload` apa adanya.
   *
   * ValidationPipe di server memakai `forbidNonWhitelisted` sebagai pencegah
   * mass assignment: satu properti asing menggagalkan SELURUH permintaan.
   * Pemanggil yang meneruskan objek formnya utuh — beserta `confirmPassword`
   * yang murni urusan form — karena itu selalu ditolak 400, dan TypeScript
   * tidak menangkapnya: pemeriksaan properti berlebih hanya berlaku untuk
   * objek literal, bukan variabel. Lapisan inilah yang tahu bentuk kawatnya,
   * jadi di sinilah penyaringannya.
   */
  changePassword(payload: ChangePasswordPayload): Promise<null> {
    return unwrap(
      apiClient.post<ApiSuccess<null>>('/auth/change-password', {
        currentPassword: payload.currentPassword,
        newPassword: payload.newPassword,
      }),
    );
  },

  /**
   * Logout selalu berhasil dari sudut pandang pengguna.
   *
   * Bila panggilan ke server gagal (offline, token sudah kedaluwarsa),
   * sesi lokal tetap harus dibersihkan — menahan pengguna tetap "masuk"
   * karena request gagal adalah perilaku yang salah.
   */
  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken();

    if (refreshToken) {
      try {
        await apiClient.post('/auth/logout', { refreshToken });
      } catch {
        // Sengaja diabaikan; pembersihan lokal di bawah tetap jalan.
      }
    }

    tokenStorage.clear();
  },
};
